<template>
  <div
      class="accordion-collapse">
    <div class="accordion-body p-0">
      <div
          :class="{ 'media-props--stack': isMultiPropertySelection }"
          class="media-props">
        <field-group
            class="media-props__position"
            label="Position on page">
          <position-controls
              :horizontal="shownAlignment.horizontal"
              :vertical="shownAlignment.vertical"
              @align="onPositionAlign"/>
        </field-group>

        <field-group
            v-if="!isMultiPropertySelection"
            label="Rotate & flip">
          <rotate-flip-controls
              @rotate="(dir) => dir === 'left' ? rotateLeft() : rotateRight()"
              @flip="(dir) => dir === 'horizontal' ? flipHorizontal() : flipVertical()"/>
        </field-group>

        <field-group
            v-if="!isMultiPropertySelection"
            label="Tools">
          <div class="media-tools media-tools--image">
            <button
                ref="transparencyButton"
                :class="{ 'is-on': popModal.isModalOpen }"
                type="button"
                class="media-tool"
                @click.stop="openTransparencyModal($event)">
              <span class="media-tool__icon"><icon name="transparency"/></span>
              <span>Opacity</span>
            </button>

            <button
                v-if="!isSVG"
                type="button"
                class="media-tool"
                @click="$emit('crop')">
              <span class="media-tool__icon"><icon name="cropimage"/></span>
              <span>Crop</span>
            </button>

            <button
                v-if="!isSVG"
                ref="adjustModalBtn"
                :class="{ 'is-on': isScaleOPen }"
                type="button"
                class="media-tool"
                @click.stop="toggleScalePop">
              <span class="media-tool__icon"><icon name="adjustments"/></span>
              <span>Adjust</span>
            </button>

            <button
                v-if="!isSVG"
                type="button"
                class="media-tool"
                @click.stop="removeImageBackground">
              <span class="media-tool__icon"><icon name="bg-remover"/></span>
              <span>Remove<br>bg</span>
            </button>

            <button
                v-if="!isSVG"
                ref="cornerRoundingBtn"
                :class="{ 'is-on': cornerRoundingModal.isOpen }"
                type="button"
                class="media-tool"
                @click.stop="openCornerRoundingModal($event)">
              <span class="media-tool__icon"><icon name="cornerRounding"/></span>
              <span>Corners</span>
            </button>
          </div>

          <!-- keep these three popups; only the trigger buttons moved -->
          <div
              v-show="popModal.isModalOpen"
              ref="transparencyModal"
              :style="getPopUpModalPositions"
              class="media-pop"
              role="dialog"
              aria-label="Transparency"
              @click.stop>
            <div class="media-pop__title">Transparency</div>
            <div class="media-pop__row">
              <label class="media-pop__label">Opacity</label>
              <input
                  :value="activeObjectForLeftPanel.effects.transparency.value"
                  :style="mediaPopTrack(activeObjectForLeftPanel.effects.transparency.value)"
                  class="media-pop__range"
                  type="range"
                  min="0"
                  max="100"
                  aria-label="Opacity"
                  @input="changeImageEffects({ name: ImageFilterTypes.OPACITY, value: parseInt($event.target.value, 10) })"
                  @mouseup="commitMediaSlider('Opacity')"
                  @touchend="commitMediaSlider('Opacity')"
                  @keyup="commitMediaSlider('Opacity')">
              <span class="media-pop__value">{{ Math.round(activeObjectForLeftPanel.effects.transparency.value) }}</span>
            </div>
          </div>

          <div
              ref="adjustModal"
              :style="[getInitialPositions, getAdjustmentModalPosition]"
              :class="[isScaleOPen ? 'show-outer-box' : 'invisible']"
              class="media-pop media-pop--adjust"
              role="dialog"
              aria-label="Adjustments"
              @click.stop>
            <div class="media-pop__title">Adjustments</div>
            <div class="media-pop__rows">
              <div
                  v-for="row in adjustEffectRows"
                  :key="row.name"
                  class="media-pop__row">
                <label class="media-pop__label">{{ row.label }}</label>
                <input
                    :value="row.value"
                    :style="mediaPopTrack(row.value)"
                    :aria-label="row.label"
                    class="media-pop__range"
                    type="range"
                    min="0"
                    max="100"
                    @input="onAdjustInput(row.name, $event.target.value)"
                    @mouseup="commitMediaSlider(row.name)"
                    @touchend="commitMediaSlider(row.name)"
                    @keyup="commitMediaSlider(row.name)">
                <label
                    v-if="row.name === 'tint'"
                    :style="{ background: tintSwatchColor }"
                    class="media-pop__swatch">
                  <input
                      :value="tintSwatchColor"
                      type="color"
                      aria-label="Tint color"
                      @input="onTintColor">
                </label>
                <span class="media-pop__value">{{ Math.round(row.value) }}</span>
              </div>
            </div>
          </div>

          <div
              v-show="cornerRoundingModal.isOpen"
              ref="cornerRoundingModal"
              :style="getCornerRoundingModalPositions"
              class="media-pop"
              role="dialog"
              aria-label="Corners"
              @click.stop>
            <div class="media-pop__title">Corners</div>
            <div class="media-pop__row">
              <label class="media-pop__label">Rounding</label>
              <input
                  :value="cornerRadiusPercent"
                  :style="mediaPopTrack(cornerRadiusPercent)"
                  class="media-pop__range"
                  type="range"
                  min="0"
                  max="100"
                  aria-label="Rounding"
                  @input="changeCornerRadius({ value: parseInt($event.target.value, 10) })"
                  @mouseup="commitMediaSlider('Rounding')"
                  @touchend="commitMediaSlider('Rounding')"
                  @keyup="commitMediaSlider('Rounding')">
              <span class="media-pop__value">{{ cornerRadiusPercent }}</span>
            </div>
          </div>
        </field-group>

        <field-group
            class="media-props__effects"
            label="Effects">
          <effect-tiles
              :options="imageEffectOptions"
              :value="imageEffectValue"
              @select="onImageEffectSelect"/>
          <effect-control-card
              v-if="imageEffectValue === 'outline'"
              :rows="imageOutlineRows"
              :color="tempImageOutlineColor"
              class="image-effect-card"
              color-aria="Open outline color picker"
              show-color
              @input="onImageOutlineInput"
              @pick="openImageEffectColorPicker"/>
          <effect-control-card
              v-if="imageEffectValue === 'shadow'"
              :rows="imageShadowRows"
              :color="tempImageShadowColor"
              class="image-effect-card"
              color-aria="Open shadow color picker"
              show-color
              @input="onImageShadowInput"
              @pick="openImageEffectColorPicker"/>
          <effect-control-card
              v-if="imageEffectValue === 'blurEdges'"
              :rows="imageBlurEdgeRows"
              class="image-effect-card"
              @input="onImageBlurEdgeInput"/>
        </field-group>
        <side-popover
            ref="imageEffectColorPopoverEl"
            :open="isColorPickerOpen && (imageEffectValue === 'outline' || imageEffectValue === 'shadow')"
            :top="imageEffectColorPopover.top"
            :left="imageEffectColorPopover.left"
            :width="303"
            :title="imageEffectValue === 'outline' ? 'Outline color' : 'Shadow color'"
            @close="isColorPickerOpen = false"
            @click.native.stop>
          <color-picker
              v-if="imageEffectValue === 'outline'"
              :key="'image-outline-color'"
              :color="tempImageOutlineColor"
              :type="getObjectType"
              :active-object="activeObjectForLeftPanel"
              :selected-objects-list="getSelectedObjectsList"
              :content-list="contentList"
              :show-gradient="false"
              display-style="inline"
              variant="panel"
              color-type="outline"/>
          <color-picker
              v-else
              :key="'image-shadow-color'"
              :active-object="activeObjectForLeftPanel"
              :color="tempImageShadowColor"
              :type="getObjectType"
              :is-for-object-shadow-effect="true"
              :selected-objects-list="getSelectedObjectsList"
              :content-list="contentList"
              :show-gradient="false"
              display-style="inline"
              variant="panel"
              color-type="shadow"/>
        </side-popover>
        <div
            v-if="!isMultiPropertySelection"
            class="outer-box-images mt-20">
          <div>
            <span class="text-box-images">Gallery</span>
            <div class="d-flex flex-wrap mt-6">
              <div
                  v-for="(imageSrc, i) in getAlternativeImageUrls"
                  :key="i + '-alt'"
                  class="me-12 mt-2 mb-10">
                <div
                    :class="[activeObjectForLeftPanel.DefaultImageUrl === imageSrc.DefaultImageUrl ? 'border-blue-primary': 'border-4', intialImageBorder === activeObjectForLeftPanel.AlternativeImageUrls[i] ? 'border-blue-primary': 'border-4']"
                    class="button-box-thumbnail thumbnail-box rounded rounded-4 p-0 border-0"
                    @click.stop="replaceImage(imageSrc)">
                  <img
                      :src="'https://pdf-processing.bankmarketingcenter.com/node/image?width=36&height=36&resizeOnly=1&image=' + imageSrc.DefaultImageUrl"
                      class="h-100 w-100 rounded-4">
                </div>
              </div>
              <div class="me-12 mt-2">
                <button
                    class="button-box-thumbnail thumbnail-box rounded rounded-4 p-0 border-0 border-4"
                    @click="toggleImageModel">
                  <icon name="page-plus"/>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import EventBus from '@/EventBus';
import ApplicationEventTypes from '@/mixins/ApplicationEventTypes';
import ImageFilterTypes from '@/mixins/ImageFilterTypes';
import { getCropUrl } from '@/components/Helper';
import { findObjectById, getPropertyApplyTargets } from '@/components/Canvas/utils/groupHelpers';
import ColorPicker from '@/components/ColorPicker/ColorPicker';
import panelContext from '@/components/LeftSection/mixins/panelContext';
import objectAlignment from '@/components/LeftSection/mixins/objectAlignment';
import objectEffects from '@/components/LeftSection/mixins/objectEffects';
import imageEffects from '@/components/LeftSection/mixins/imageEffects';
import objectTransform from '@/components/LeftSection/mixins/objectTransform';
import mediaTools from '@/components/LeftSection/mixins/mediaTools';
import sidePopover from '@/components/LeftSection/mixins/sidePopover';
import FieldGroup from '@/components/LeftSection/ui/FieldGroup';
import PositionControls from '@/components/LeftSection/ui/PositionControls';
import RotateFlipControls from '@/components/LeftSection/ui/RotateFlipControls';
import EffectTiles from '@/components/LeftSection/ui/EffectTiles';
import EffectControlCard from '@/components/LeftSection/ui/EffectControlCard';
import SidePopover from '@/components/LeftSection/ui/SidePopover';

export default {
  name:       'ImagePanel',
  components: {
    'color-picker':         ColorPicker,
    'field-group':          FieldGroup,
    'position-controls':    PositionControls,
    'rotate-flip-controls': RotateFlipControls,
    'effect-tiles':         EffectTiles,
    'effect-control-card':  EffectControlCard,
    'side-popover':         SidePopover,
  },
  mixins: [ImageFilterTypes.Mixin, panelContext, objectAlignment, objectEffects, imageEffects, objectTransform, mediaTools, sidePopover],
  props:  {
    width:       { type: Number, default: 0 },
    height:      { type: Number, default: 0 },
    productCrop: { type: Object, default: null },
  },
  data() {
    return {
      imageEffectSelection: 'none',
      intialImageBorder:    '',
      cornerRoundingModal:  {
        isOpen:   false,
        position: { x: 0, y: 0 },
      },
    };
  },
  computed: {
    cornerRadiusPercent() {
      const radius = this.activeObjectForLeftPanel && this.activeObjectForLeftPanel.cornerRadius;
      return Math.round((Number(radius) || 0) * 100);
    },
    imageEffectOptions() {
      return [
        { value: 'none', label: 'None', preview: 'none' },
        { value: 'outline', label: 'Outline', preview: 'outline' },
        { value: 'shadow', label: 'Shadow', preview: 'shadow' },
        { value: 'blurEdges', label: 'Blur edges', preview: 'blur' },
      ];
    },
    imageEffectValue() {
      return this.imageEffectSelection;
    },
    imageBlurEdgeRows() {
      return [{
        key:     'distance',
        label:   'Distance',
        value:   this.objectBlurEdgesDistance,
        min:     0,
        max:     100,
        step:    1,
        fill:    Math.round(Number(this.objectBlurEdgesDistance)),
        display: this.objectBlurEdgesDistance,
      }];
    },
    getCornerRoundingModalPositions() {
      return {
        left: `${this.cornerRoundingModal.position.x}px`,
        top:  `${this.cornerRoundingModal.position.y}px`,
      };
    },
    getAlternativeImageUrls() {
      return this.activeObjectForLeftPanel.AlternativeImageUrlsResVersion;
    },
  },
  watch: {
    // The imageEffects mixin syncs the slider values; this keeps the selected Effects tile in sync.
    activeObject: {
      handler(newVal) {
        const panelObj = this.effectPanelObject(newVal);
        if (panelObj && panelObj.type === 'image' && panelObj.ImageType === 'Image') {
          const effect              = panelObj.effect;
          this.imageEffectSelection = !effect || effect === 'none' ? 'none' : effect;
        }
      },
      immediate: true,
      deep:      true,
    },
  },
  mounted() {
    this.activeObject && this.activeObject.AlternativeImageUrls && (this.intialImageBorder = this.activeObject.AlternativeImageUrls[0]);
  },
  methods: {
    removeImageBackground() {
      EventBus.$emit('showHideLoader', true);
      const object   = this.activeObjectForLeftPanel;
      const cropUrl  = getCropUrl(object);
      const imageUrl = cropUrl ? cropUrl.split('&src=')[1] : (object.HighResPath || object.DefaultImageUrl);

      const urlSearchPart      = window.location.search;
      const newUrlSearchParams = new URLSearchParams(urlSearchPart);

      const userId    = newUrlSearchParams.has('userId') ? newUrlSearchParams.get('userId') : '21d29854-fb88-45e6-9a12-b1f4e6c67d45';
      const bankId    = newUrlSearchParams.has('bankId') ? newUrlSearchParams.get('bankId') : 'fde5ed9d-816f-43fd-89f3-97a542947193';
      const domainUrl = this.$store.getters.getBMCInstance === 'marketingcenter.source4' ? this.$store.getters.getBMCInstance : `www.${this.$store.getters.getBMCInstance}`;
      const url       = `${this.$store.getters.getBaseUrl}/web_service.aspx?method=RemoveBackgroundV3&imageURL=${imageUrl}&domainURL=https://${domainUrl}.com/&bankId=${bankId}&userId=${userId}`;
      console.log('url', url);
      const oReq = new XMLHttpRequest();
      oReq.addEventListener('load', (Response) => {
        const parser               = new DOMParser();
        const xmlParsed            = parser.parseFromString(Response.target.response, 'text/xml');
        let removeBackgroundResult = xmlParsed.getElementsByTagName('RemoveBackgroundV3Result');
        removeBackgroundResult[0] && (removeBackgroundResult = removeBackgroundResult[0].innerHTML);
        if (removeBackgroundResult.includes('invalid_image_url') || removeBackgroundResult.includes('error')) {
          EventBus.$emit('showHideLoader', false);
          EventBus.$emit(ApplicationEventTypes.ModalEvents.TOGGLE_CONFIRMATION_MODAL, {
            request: 'SaveOrderReject',
            message: 'Could not remove background',
          });
          return;
        }
        EventBus.$emit('replaceBgRemoverImageUrl', removeBackgroundResult);
      });
      oReq.addEventListener('error', (e) => {
        console.error(e);
        EventBus.$emit('showHideLoader', false);
      });
      oReq.open('GET', url);
      oReq.send();
    },
    onImageEffectSelect(value) {
      const next                = value || 'none';
      this.imageEffectSelection = next;
      this.getSelectedObjectsList.forEach((object) => {
        if (!object || object.type !== 'image') {
          return;
        }
        const live = findObjectById(this.contentList, object.Id) || object;
        this.$set(live, 'effect', next);
      });
      if (next === 'none') {
        this.removeImageEffect();
      } else if (next === 'outline') {
        this.applyOutlineOnImage();
      } else if (next === 'shadow') {
        this.applyShadowOnImage();
      } else if (next === 'blurEdges') {
        this.applyBlurEdgesOnImage();
      }
    },
    onImageBlurEdgeInput(payload) {
      this.updateImageBlurEdgesProperty('distance', parseFloat(payload.value));
    },
    applyBlurEdgesOnImage() {
      this.getSelectedObjectsList.forEach((object) => {
        if (object.type === 'image') {
          this.applyBlurEdgesOnImageById(object.Id);
        }
      });
      this.emitUndoRedoRequest('Image BlurEdges');
      EventBus.$emit(ApplicationEventTypes.Canvas.CANVAS_RE_RENDER);
      this.restoreSelectionAfterChange();
      this.emitUndoRedoRequest('applyBlurEdgesOnImage');
    },
    applyBlurEdgesOnImageById(id) {
      const selectedObject = findObjectById(this.contentList, id);
      if (!selectedObject) { return; }

      const hasCurrentDistance = selectedObject.blurEdges && typeof selectedObject.blurEdges.distance !== 'undefined' && selectedObject.blurEdges.distance !== null;
      const distance           = hasCurrentDistance
          ? selectedObject.blurEdges.distance
          : ((typeof this.objectBlurEdgesDistance !== 'undefined' && this.objectBlurEdgesDistance !== null)
              ? this.objectBlurEdgesDistance
              : 6);
      const newDistance        = Math.min(100, Math.max(0, Math.round(parseFloat(distance) || 0)));

      selectedObject.effect    = 'blurEdges';
      selectedObject.blurEdges = {
        distance: newDistance,
      };

      EventBus.$emit(ApplicationEventTypes.LayerPanelEvents.UPDATE_DATA_ON_LAYER_SWAP, this.contentList);
    },
    updateImageBlurEdgesProperty(property, value) {
      const newProperty = property === 'distance'
          ? Math.min(100, Math.max(0, Math.round(parseFloat(value) || 0)))
          : value;
      if (property === 'distance') {
        this.objectBlurEdgesDistance = newProperty;
      }

      this.getSelectedObjectsList.forEach((object) => {
        if (object.type === 'image' || object.ImageType === 'SVGImage') {
          const selectedObject = findObjectById(this.contentList, object.Id);

          if (!selectedObject) { return; }

          if (!selectedObject.blurEdges) {
            this.$set(selectedObject, 'blurEdges', {});
          }

          this.$set(selectedObject.blurEdges, property, newProperty);
          this.updateBlurEdgesMaskPreview(selectedObject, newProperty);
        }
      });

      this.emitUndoRedoRequest('Update Image BlurEdges');
    },
    updateBlurEdgesMaskPreview(selectedObject, distance) {
      const element = document.querySelector(`.canvas-main-html-container [id='${selectedObject.Id}']`);
      if (!element) { return; }

      const parsedDistance = parseFloat(distance);
      if (Number.isNaN(parsedDistance)) { return; }
      const blurDistance = Math.max(parsedDistance, 0);

      if (blurDistance === 0) {
        element.style.maskSize       = '';
        element.style.WebkitMaskSize = '';
        element.removeAttribute('data-blur-edges-distance');
        return;
      }

      let reductionPercent = 0;
      if (blurDistance <= 5) {
        reductionPercent = (blurDistance / 5) * 0.8;
      } else {
        const progressAfterSix = Math.min((blurDistance - 6) / 94, 1);
        reductionPercent       = 1.25 + (progressAfterSix * (20 - 1.25));
      }
      const sizePercentage = 100 - reductionPercent;
      const finalSize      = `${Math.max(sizePercentage, 10)}%`;

      element.style.maskSize       = `${finalSize} ${finalSize}`;
      element.style.WebkitMaskSize = `${finalSize} ${finalSize}`;
      element.setAttribute('data-blur-edges-distance', blurDistance);
    },
    replaceImage(ImgSrc) {
      this.intialImageBorder = '';
      EventBus.$emit('onReplaceImage', ImgSrc);
    },
    toggleImageModel() {
      if (this.$store.getters.isImageModel == false) {
        this.$store.commit('setImageModel', true);
      } else {
        this.$store.commit('setImageModel', false);
      }
    },
    openCornerRoundingModal() {
      this.isScaleOPen                  = false;
      this.popModal.isModalOpen         = false;
      this.cornerRoundingModal.isOpen   = !this.cornerRoundingModal.isOpen;
      const selectedButton              = this.$refs.cornerRoundingBtn.getBoundingClientRect();
      this.cornerRoundingModal.position = {
        x: selectedButton.x + 43,
        y: selectedButton.y - 3,
      };
    },
    changeCornerRadius(params) {
      const ratio   = Math.min(100, Math.max(0, Number(params.value) || 0)) / 100;
      const targets = getPropertyApplyTargets(
          this.contentList,
          this.selectedObjectsList,
          this.activeObject,
      );
      const live    = this.activeObjectForLeftPanel;
      if (live && !targets.some(item => item && live && item.Id === live.Id)) {
        targets.push(live);
      }
      targets.forEach((activeObject) => {
        if (!activeObject || activeObject.type !== 'image') {
          return;
        }
        this.$set(activeObject, 'cornerRadius', ratio);
        if (ratio > 0) {
          this.$set(activeObject, 'circleCrop', false);
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../styles/lp-tokens';
@import '../styles/lp-media';
</style>
